<template>
  <div class="order-list-page">
    <!-- 进入页面加载中 -->
    <div v-if="pageLoading && orderList.length === 0" class="loading-wrap">
      <van-loading type="spinner" color="#1989fa" />
      <span>加载中...</span>
    </div>

    <template v-else>
      <!-- 顶部搜索栏 -->
      <div class="search-bar">
        <van-search v-model="searchKey" placeholder="搜索收据号/学员姓名/手机号" shape="round" @search="handleSearch"
          @clear="handleSearch">
          <template #right-icon>
            <van-icon name="search" @click="handleSearch" />
          </template>
        </van-search>
      </div>

      <!-- 状态快捷切换 -->
      <div class="quick-tabs">
        <div v-for="tab in quickTabs" :key="tab.value" :class="['quick-tab', { active: activeTab === tab.value }]"
          @click="switchTab(tab.value)">
          {{ tab.label }}
        </div>
      </div>

      <!-- 订单列表 -->
      <div class="order-list">
        <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
          <template v-if="orderList.length > 0">
            <van-list v-model:loading="loading" :finished="finished" finished-text="没有更多了" @load="loadList">
            <div
              v-for="item in orderList"
              :key="item.orderId"
              class="order-card"
              :class="'card-status-' + getStatusType(item.orderStatus)"
              @click="goDetail(item)"
            >
              <div class="oc-content">
                <!-- 顶部：学员 + 年级 + 状态 -->
                <div class="oc-top">
                  <div class="oc-student">
                    <span class="oc-name">{{ item.studentName }}</span>
                    <span class="oc-grade" v-if="getGradeLabel(item.grade) !== '-'">{{ getGradeLabel(item.grade) }}</span>
                    <van-tag v-if="item.isSpecialDiscount" size="small" type="warning" round>特</van-tag>
                  </div>
                  <div class="oc-status">
                    <span class="oc-status-dot"></span>
                    <span class="oc-status-text">{{ getStatusLabel(item.orderStatus) }}</span>
                  </div>
                </div>

                <!-- 信息网格 -->
                <div class="oc-info-grid">
                  <div class="oc-info-item">
                    <span class="oc-info-label">收据号</span>
                    <span class="oc-info-val">{{ item.receiptNo || '-' }}</span>
                  </div>
                  <div class="oc-info-item">
                    <span class="oc-info-label">合同金额</span>
                    <span class="oc-info-val">¥{{ formatMoney(item.courseAmount) }}</span>
                  </div>
                  <div class="oc-info-item">
                    <span class="oc-info-label">实收</span>
                    <span class="oc-info-val">¥{{ formatMoney(item.realPay) }}</span>
                  </div>
                  <div class="oc-info-item">
                    <span class="oc-info-label">欠费</span>
                    <span class="oc-info-val" :class="{ danger: item.arrearage > 0 }">
                      {{ item.arrearage > 0 ? '¥' + formatMoney(item.arrearage) : '-' }}
                    </span>
                  </div>
                  <div class="oc-info-item" v-if="item.textBookAmount > 0">
                    <span class="oc-info-label">教材费</span>
                    <span class="oc-info-val">¥{{ formatMoney(item.textBookAmount) }}</span>
                  </div>
                  <div class="oc-info-item" v-if="item.filingAmount > 0">
                    <span class="oc-info-label">建档费</span>
                    <span class="oc-info-val">¥{{ formatMoney(item.filingAmount) }}</span>
                  </div>
                  <div class="oc-info-item">
                    <span class="oc-info-label">课时</span>
                    <span class="oc-info-val">{{ item.buyClassHourCount || '-' }}{{ item.buyClassHourCount ? '课时' : '' }}</span>
                  </div>
                  <div class="oc-info-item">
                    <span class="oc-info-label">缴费日期</span>
                    <span class="oc-info-val">{{ formatDate(item.payTime) }}</span>
                  </div>
                </div>

                <!-- 底部：规划师 + 校区 -->
                <div class="oc-meta">
                  <span class="oc-meta-item"><van-icon name="friends-o" size="11" /> {{ item.plannerName || '-' }}</span>
                  <span class="oc-meta-item" v-if="!isSingleSchool">{{ item.schoolName }}</span>
                </div>

                <!-- 操作按钮 -->
                <div v-if="hasAnyAction(item)" class="oc-actions" @click.stop>
                  <van-button v-if="canEdit(item)" size="small" plain type="primary" @click="handleUpdate(item)">修改</van-button>
                  <van-button v-if="canSubmitAudit(item)" size="small" plain type="primary" @click="handleSubmitAudit(item)">提交审核</van-button>
                  <van-button v-if="canAudit(item)" size="small" plain type="success" @click="handleAudit(item)">审核</van-button>
                  <van-button v-if="canRevert(item)" size="small" plain type="warning" @click="handleRevert(item)">撤回</van-button>
                  <van-button v-if="canPay(item)" size="small" plain type="danger" @click="handlePay(item)">支付</van-button>
                  <van-button v-if="canReceipt(item)" size="small" plain type="default" @click="handleReceipt(item)">收据</van-button>
                  <van-button v-if="canVoid(item)" size="small" plain type="danger" @click="handleVoid(item)">作废</van-button>
                </div>
              </div>
            </div>
          </van-list>
        </template>
        <van-empty v-else-if="!loading" description="暂无订单数据" />
      </van-pull-refresh>
    </div>

    <!-- 新增浮动按钮 -->
    <div v-if="hasPerm('financial:order:add')" class="add-fab" @click="handleAdd">
      <van-icon name="plus" size="24" color="#fff" />
    </div>
    </template>

    <!-- 审核操作面板：点击审核后选择通过/不通过 -->
    <van-action-sheet
      v-model:show="showAuditSheet"
      :actions="auditActions"
      cancel-text="取消"
      description="请选择审核结果"
      @select="onAuditSelect"
    />

    <!-- 驳回原因弹窗 -->
    <van-dialog v-model:show="showAuditRemark" title="驳回原因" show-cancel-button @confirm="confirmAuditFail">
      <div class="audit-remark-dialog">
        <van-field v-model="auditRemark" type="textarea" rows="3" autosize placeholder="请输入驳回原因" />
      </div>
    </van-dialog>

    <!-- 支付金额输入弹窗 -->
    <van-dialog v-model:show="showPayAmount" title="支付金额" show-cancel-button @confirm="confirmPayAmount">
      <div class="pay-dialog">
        <div class="pay-field">
          <span class="pay-label">欠费金额</span>
          <span class="pay-value arrearage">¥{{ currentOrder?.arrearage }}</span>
        </div>
        <div class="pay-field">
          <span class="pay-label">支付金额</span>
          <div class="pay-input-wrap">
            <span class="pay-prefix">¥</span>
            <input ref="payInputRef" v-model.number="payAmount" type="number" class="pay-input" placeholder="请输入" />
          </div>
        </div>
      </div>
    </van-dialog>

    <!-- 支付二维码弹窗 -->
    <PayQrcodeDialog v-model:show="showPayQrcode" :order-id="payQrcodeOrderId" :amount="payQrcodeAmount"
      :company-id="currentOrder?.companyId" @success="loadList(true)" />

    <!-- 收据图片弹窗：html2canvas 截图，长按可保存/转发 -->
    <ReceiptDialog v-model:show="showReceipt" :order-id="receiptOrderId" />
  </div>
</template>

<script>
// 显式声明组件名，供 App.vue 的 <keep-alive :include> 匹配缓存
export default { name: 'OrderList' }
</script>

<script setup>
import { ref, computed, onMounted, onActivated, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { showConfirmDialog, showSuccessToast, showFailToast } from 'vant'
import {
  listOrder, toAudit, revertAudit, delOrder, wxAudit
} from '@/api/order'
import { getSchoolDicts } from '@/api/dict'
import { getUserInfo } from '@/utils/authUtils'
import { useDict, selectDictLabel } from '@/utils/dict'
import { ensureWecomLogin } from '@/utils/wecomAuth'
import PayQrcodeDialog from './components/PayQrcodeDialog.vue'
import ReceiptDialog from './components/ReceiptDialog.vue'

const router = useRouter()
const route = useRoute()

const { audit_status, grade_list } = useDict('audit_status', 'grade_list')

// 权限
const userInfo = ref(getUserInfo() || {})
function hasPerm(perm) {
  return userInfo.value?.permissions?.includes(perm) || false
}

// 搜索
const searchKey = ref('')

// 查询参数
const queryParams = ref({
  pageNum: 1,
  pageSize: 10,
  status: undefined,
  statusIn: undefined,
  params: {}
})

// 快捷状态 tab
const quickTabs = [
  { label: '全部', value: 'all' },
  { label: '待审核', value: 'pending' },
  { label: '审核成功', value: 'success' },
  { label: '审核失败', value: 'fail' }
]
const activeTab = ref('all')

// 校区
const schoolList = ref([])
const isSingleSchool = computed(() => schoolList.value.length === 1)

// 列表
const orderList = ref([])
const loading = ref(false)
const pageLoading = ref(false)
const finished = ref(false)
const refreshing = ref(false)

// 支付
const showPayAmount = ref(false)
const currentOrder = ref(null)
const payAmount = ref(0)
const payInputRef = ref(null)

// 收据
const showReceipt = ref(false)
const receiptOrderId = ref('')

// 审核
const showAuditSheet = ref(false)
const showAuditRemark = ref(false)
const auditRemark = ref('')
const currentAuditOrder = ref(null)
const auditActions = [
  { name: '审核通过', value: 1 },
  { name: '审核不通过', value: 0 }
]

// 动态调整支付金额输入框宽度，使其刚好包裹内容，保证与欠费金额右对齐
function adjustInputWidth() {
  nextTick(() => {
    const el = payInputRef.value
    if (!el) return
    // 用 canvas 精确测量文本宽度
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    ctx.font = getComputedStyle(el).font
    const text = String(el.value || el.placeholder || '')
    const width = ctx.measureText(text).width
    el.style.width = Math.max(width + 2, 24) + 'px'
  })
}
watch(payAmount, () => adjustInputWidth())
const showPayQrcode = ref(false)
const payQrcodeOrderId = ref('')
const payQrcodeAmount = ref(0)

onMounted(async () => {
  pageLoading.value = true
  await ensureWecomLogin(route.query.code)
  userInfo.value = getUserInfo() || {}
  await loadSchools()
  loadList(true)
})

// keep-alive 缓存组件被激活时触发：仅当从新增/编辑表单提交返回（已打标）时刷新列表；
// 普通详情返回不刷新，以保留 DOM 与滚动位置
onActivated(() => {
  if (sessionStorage.getItem('__orderListNeedRefresh') === '1') {
    sessionStorage.removeItem('__orderListNeedRefresh')
    pageLoading.value = true
    loadList(true)
  }
})

async function loadSchools() {
  try {
    const res = await getSchoolDicts()
    schoolList.value = res || []
  } catch (e) {
    schoolList.value = []
  }
}

async function loadList(reset = false) {
  if (reset) {
    queryParams.value.pageNum = 1
    orderList.value = []
    finished.value = false
  }
  loading.value = true
  try {
    const params = { ...queryParams.value }
    if (searchKey.value) {
      params.keyword = searchKey.value
    }
    const res = await listOrder(params)
    const rows = res?.rows || res || []
    orderList.value = reset ? rows : [...orderList.value, ...rows]
    if (rows.length < queryParams.value.pageSize) {
      finished.value = true
    } else {
      queryParams.value.pageNum++
    }
  } catch (e) {
    finished.value = true
  } finally {
    loading.value = false
    refreshing.value = false
    pageLoading.value = false
  }
}

function onRefresh() {
  loadList(true)
}

function handleSearch() {
  loadList(true)
}

// 快捷 tab 切换
function switchTab(value) {
  activeTab.value = value
  queryParams.value.status = undefined
  queryParams.value.statusIn = undefined
  if (value === 'pending') {
    queryParams.value.statusIn = '1,5,7'
  } else if (value === 'success') {
    queryParams.value.status = 3
  } else if (value === 'fail') {
    queryParams.value.status = 2
  }
  loadList(true)
}

// 状态判断
function canEdit(row) {
  return hasPerm('financial:order:edit') && [0, 2, 6].includes(row.orderStatus)
}
function canSubmitAudit(row) {
  return hasPerm('financial:order:edit') && [0, 2, 6].includes(row.orderStatus)
}
function canAudit(row) {
  if (row.orderStatus === 7) return hasPerm('special_audit')
  return hasPerm('financial:order:audit') &&
    ((row.orderStatus === 1 && !row.isSpecialDiscount) || row.orderStatus === 5)
}
function canRevert(row) {
  return hasPerm('financial:order:edit') &&
    (row.orderStatus === 7 || (row.orderStatus === 1 && !row.isSpecialDiscount))
}
function canPay(row) {
  // 5万上限仅博尔思（companyId=1，数币监管支付渠道）保留；博思走富友收款码不限此额
  const withinSbLimit = Number(row.companyId) !== 1 || row.shouldPay <= 50000
  return hasPerm('financial:order:edit') &&
    row.arrearage > 0 && withinSbLimit &&
    ![3, 4].includes(row.orderStatus)
}
function canVoid(row) {
  return hasPerm('financial:orderRefund:remove') && ![3, 4].includes(row.orderStatus)
}
// 收据：与 PC 列表口径一致，作废订单不出（orderStatus 4=作废）
function canReceipt(row) {
  return row.orderStatus !== 4
}

function getStatusLabel(status) {
  return selectDictLabel(audit_status.value, status)
}
function getGradeLabel(grade) {
  return selectDictLabel(grade_list.value, grade)
}
function getStatusType(status) {
  const map = { 0: 'warning', 1: 'primary', 2: 'danger', 3: 'success', 4: 'default', 5: 'primary', 6: 'warning', 7: 'warning' }
  return map[status] || 'default'
}

function formatDate(date) {
  if (!date) return '-'
  const d = new Date(date)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function formatMoney(val) {
  const num = Number(val) || 0
  return num.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

function hasAnyAction(row) {
  return canEdit(row) || canSubmitAudit(row) || canAudit(row) || canRevert(row) || canPay(row) || canVoid(row) || canReceipt(row)
}

// 操作
function goDetail(row) {
  router.push({ path: '/orderView', query: { id: row.orderId } })
}

function handleAdd() {
  router.push({ path: '/orderForm' })
}

function handleUpdate(row) {
  router.push({ path: '/orderForm', query: { id: row.orderId } })
}

async function handleSubmitAudit(row) {
  try {
    await showConfirmDialog({ title: '', message: '确认提交审核？' })
    await toAudit(row.orderId)
    showSuccessToast('提交成功')
    loadList(true)
  } catch (e) { /* cancel */ }
}

async function handleRevert(row) {
  try {
    await showConfirmDialog({ title: '', message: '确认撤回审核？' })
    await revertAudit(row.orderId)
    showSuccessToast('撤回成功')
    loadList(true)
  } catch (e) { /* cancel */ }
}

function handleAudit(row) {
  currentAuditOrder.value = row
  showAuditSheet.value = true
}

function onAuditSelect(action) {
  showAuditSheet.value = false
  if (action.value === 1) {
    handleAuditPass()
  } else {
    handleAuditFail()
  }
}

function handleAuditPass() {
  showConfirmDialog({ title: '', message: '确认该订单审核通过？' })
    .then(async () => {
      await wxAudit({ orderId: currentAuditOrder.value.orderId, auditResult: 1, remark: '' })
      showSuccessToast('审核通过')
      loadList(true)
    })
    .catch(() => { /* cancel */ })
}

function handleAuditFail() {
  auditRemark.value = ''
  showAuditRemark.value = true
}

async function confirmAuditFail() {
  if (!auditRemark.value) {
    showFailToast('请输入驳回原因')
    return
  }
  await wxAudit({ orderId: currentAuditOrder.value.orderId, auditResult: 0, remark: auditRemark.value })
  showSuccessToast('已驳回')
  loadList(true)
}

async function handleVoid(row) {
  try {
    await showConfirmDialog({ title: '', message: '确认作废该订单？' })
    await delOrder(row.orderId)
    showSuccessToast('作废成功')
    loadList(true)
  } catch (e) { /* cancel */ }
}

function handlePay(row) {
  currentOrder.value = row
  payAmount.value = row.arrearage
  showPayAmount.value = true
  adjustInputWidth()
}

function handleReceipt(row) {
  receiptOrderId.value = row.orderId
  showReceipt.value = true
}

async function confirmPayAmount() {
  if (!payAmount.value || payAmount.value <= 0) {
    showFailToast('支付金额必须大于0')
    return
  }
  if (payAmount.value > currentOrder.value.arrearage) {
    showFailToast('支付金额不能超过欠费金额')
    return
  }
  showPayAmount.value = false
  payQrcodeOrderId.value = currentOrder.value.orderId
  payQrcodeAmount.value = payAmount.value
  showPayQrcode.value = true
}
</script>

<style scoped>
/* 进入页面加载中（与订单详情一致） */
.loading-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  gap: 10px;
  color: #969799;
  font-size: 14px;
}

.order-list-page {
  min-height: 100vh;
  background: #f5f7fa;
  padding-bottom: 20px;
}

.search-bar {
  position: sticky;
  top: 0;
  z-index: 100;
  background: #fff;
}

/* 快捷状态 tab */
.quick-tabs {
  display: flex;
  background: #fff;
  padding: 0 12px;
  border-bottom: 1px solid #ebedf0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.quick-tabs::-webkit-scrollbar {
  display: none;
}

.quick-tab {
  flex-shrink: 0;
  padding: 10px 14px;
  font-size: 14px;
  color: #666;
  position: relative;
  cursor: pointer;
}

.quick-tab.active {
  color: #1989fa;
  font-weight: 600;
}

.quick-tab.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 2px;
  background: #1989fa;
  border-radius: 1px;
}

/* 列表 */
.order-list {
  padding: 12px;
}

/* 订单卡片 */
.order-card {
  background: #fff;
  border-radius: 12px;
  margin-bottom: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.oc-content {
  padding: 14px;
}

/* 卡片顶部 */
.oc-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2px;
}
.oc-student {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}
.oc-name {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a1a;
}
.oc-grade {
  font-size: 11px;
  color: #1989fa;
  background: rgba(25,137,250,0.08);
  padding: 1px 6px;
  border-radius: 4px;
  font-weight: 500;
}

/* 状态：圆点+文字 */
.oc-status {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  margin-left: 8px;
}
.oc-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c8c9cc;
}
.card-status-primary .oc-status-dot { background: #1989fa; }
.card-status-success .oc-status-dot { background: #07c160; }
.card-status-danger .oc-status-dot { background: #ee0a24; }
.card-status-warning .oc-status-dot { background: #ff976a; }
.oc-status-text {
  font-size: 13px;
  font-weight: 500;
  color: #969799;
}
.card-status-primary .oc-status-text { color: #1989fa; }
.card-status-success .oc-status-text { color: #07c160; }
.card-status-danger .oc-status-text { color: #ee0a24; }
.card-status-warning .oc-status-text { color: #ff976a; }

/* 信息列表 */
.oc-info-grid {
  padding: 8px 0;
  margin-bottom: 10px;
  border-bottom: 1px dashed #f2f3f5;
}
.oc-info-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 13px;
}
.oc-info-label {
  color: #969799;
  flex-shrink: 0;
}
.oc-info-val {
  color: #323233;
  font-weight: 500;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 65%;
}
.oc-info-val.danger {
  color: #ee0a24;
}

/* 底部信息 */
.oc-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #969799;
}
.oc-meta-item {
  display: flex;
  align-items: center;
  gap: 3px;
}
.oc-meta-item .van-icon {
  color: #c8c9cc;
}

/* 操作按钮 */
.oc-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 10px;
  margin-top: 10px;
  border-top: 1px solid #f2f3f5;
}
.oc-actions .van-button {
  flex: 1;
  min-width: 64px;
}

.add-fab {
  position: fixed;
  right: 20px;
  bottom: 30px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1989fa, #0066cc);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(25, 137, 250, 0.4);
  z-index: 99;
}

/* 补缴欠费弹窗 - 简洁对称 */
.pay-dialog {
  padding: 4px 20px 8px;
}
.pay-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0;
}
.pay-field + .pay-field {
  border-top: 1px solid #f2f3f5;
}
.pay-label {
  font-size: 14px;
  color: #646566;
  flex-shrink: 0;
}
.pay-value {
  font-size: 16px;
  font-weight: 600;
  color: #323233;
}
.pay-value.arrearage {
  color: #ee0a24;
}
.pay-input-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  background: #f7f8fa;
  border-radius: 6px;
  padding: 4px 0 4px 10px;
  min-width: 100px;
}
.pay-input-wrap:focus-within {
  border-color: #1989fa;
  background: #fff;
}
.pay-prefix {
  font-size: 16px;
  font-weight: 600;
  color: #323233;
}
.pay-input {
  width: auto;
  border: none;
  background: transparent;
  font-size: 16px;
  font-weight: 600;
  color: #323233;
  text-align: left;
  outline: none;
  padding: 2px 0;
  -moz-appearance: textfield;
}
.pay-input::-webkit-outer-spin-button,
.pay-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.pay-input::placeholder {
  color: #c8c9cc;
  font-weight: 400;
}
.audit-remark-dialog {
  padding: 12px 0;
}
</style>
